import ArrowSymbol from 'ol/style/RegularShape.js';
import Point from 'ol/geom/Point.js';
import { fromLonLat } from 'ol/proj.js';
import { Style, Text, Fill, Stroke, Circle as MarkerDot } from 'ol/style.js';
import type { ProjectionLike } from 'ol/proj.js';
import type { DataFeature, DataLayerStyle } from '../shared/data-layer.js';
import { routeProfileData } from './route-profile-data.js';

export function routeDistanceMarkers(feature: DataFeature, spacing: number) {
  const data = routeProfileData(feature);
  if (!data || spacing <= 0) return [];
  const markers: { coordinate: number[]; distance: number }[] = [];
  let next = spacing;
  for (let i = 1; i < data.points.length; i++) {
    const a = data.points[i - 1]; const b = data.points[i];
    if (a.segment !== b.segment || b.distance === a.distance) continue;
    while (next <= b.distance && next < data.distance) {
      const fraction = (next - a.distance) / (b.distance - a.distance);
      const longitude = ((b.coordinate[0] - a.coordinate[0] + 540) % 360) - 180;
      markers.push({ coordinate: [a.coordinate[0] + longitude * fraction, a.coordinate[1] + (b.coordinate[1] - a.coordinate[1]) * fraction], distance: next });
      next += spacing;
    }
  }
  return markers;
}

export function createRouteMapStyle(feature: DataFeature, style: DataLayerStyle, projection: ProjectionLike) {
  const data = routeProfileData(feature);
  const points = data?.points.map(point => ({ ...point, xy: fromLonLat(point.coordinate, projection) })) ?? [];
  const length = points.reduce((total, point, i) => i && point.segment === points[i - 1].segment ? total + Math.hypot(point.xy[0] - points[i - 1].xy[0], point.xy[1] - points[i - 1].xy[1]) : total, 0);
  const marks = routeDistanceMarkers(feature, (style.routeMarkerKm ?? 10) * 1000);
  let previousResolution = -1; let cached: Style[] = [];
  return (resolution: number): Style[] => {
    if (style.opacity === 0) return [];
    if (resolution === previousResolution) return cached;
    previousResolution = resolution;
    const result: Style[] = [];
    if (style.routeArrows !== false) {
      const spacing = Math.max(100 * resolution, length / 500);
      let remaining = spacing / 2;
      for (let i = 1; i < points.length; i++) {
        const a = points[i - 1]; const b = points[i];
        if (a.segment !== b.segment) { remaining = spacing / 2; continue; }
        const dx = b.xy[0] - a.xy[0]; const dy = b.xy[1] - a.xy[1];
        const length = Math.hypot(dx, dy);
        if (!length) continue;
        for (; remaining <= length; remaining += spacing) {
          result.push(new Style({ geometry: new Point([a.xy[0] + dx * remaining / length, a.xy[1] + dy * remaining / length]),
            image: new ArrowSymbol({ points: 3, radius: 7, rotation: Math.PI / 2 - Math.atan2(dy, dx), rotateWithView: true,
              fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: style.color, width: 1.5 }), declutterMode: 'none' }) }));
        }
        remaining -= length;
      }
    }
    let last: number[] | undefined;
    for (const mark of marks) {
      const xy = fromLonLat(mark.coordinate, projection);
      if (last && Math.hypot(xy[0] - last[0], xy[1] - last[1]) / resolution < 65) continue;
      last = xy;
      result.push(new Style({ geometry: new Point(xy), image: new MarkerDot({ radius: 3, fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: style.color, width: 1.5 }) }), text: new Text({ text: `${mark.distance / 1000} km`, font: '600 12px sans-serif',
        offsetY: -16, padding: [4, 6, 4, 6], fill: new Fill({ color: style.color }), backgroundFill: new Fill({ color: '#ffffff' }),
        backgroundStroke: new Stroke({ color: style.color, width: 1 }), overflow: true }) }));
    }
    cached = result;
    return result;
  };
}
